import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service, Slack, Timeframe } from 'webapp-types';
import type { GoalDeliveryType, GoalOwnerScheduleType, ScheduleType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { equal } from 'webapp/utils/common';
import {
	getHistoryValuesFromValueRows,
	groupValueRowsByTimeUnit,
	recalculateAllValues,
	TargetEndDateFormat,
} from 'webapp/utils/goal-value-rows';
import { isTimestampBefore } from 'webapp/utils/number';

import definite from 'webapp/helpers/definite';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormDatetimePickerComponent from 'webapp/components/form/datetime-picker';
import GoalInlineGroupComponent from 'webapp/components/goal/inline-group';
import GoalInlineOwnerComponent from 'webapp/components/goal/inline-owner';
import GoalNotificationsOwnerComponent from 'webapp/components/goal/notifications/owner';
import GoalNotificationsSettingsComponent from 'webapp/components/goal/notifications/settings';
import GoalsInsightsAdditionalBoxesComponent from 'webapp/components/goals/insights/additional-boxes/index';
import GoalsInsightsDataVisualizationComponent, {
	GoalInsightsChartState,
} from 'webapp/components/goals/insights/data-visualization';
import GoalsInsightsDescriptionComponent from 'webapp/components/goals/insights/description';
import GoalsInsightsLogsComponent from 'webapp/components/goals/insights/logs';
import GoalsInsightsSubgoalsComponent from 'webapp/components/goals/insights/subgoals';
import GoalsInsightsToolbarComponent from 'webapp/components/goals/insights/toolbar';
import GoalsProgressComponent from 'webapp/components/goals/progress';
import GoalsStatusComponent from 'webapp/components/goals/status/index';
import GoalsTimeframeDropdownComponent from 'webapp/components/goals/timeframe/dropdown';
import GoalsVisibilityInlineComponent from 'webapp/components/goals/visibility/inline';
import OwnerSelectComponent from 'webapp/components/owner-select';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Goal } from 'webapp/models-custom';
import type GoalValueRow from 'webapp/models-custom/goal/value-row';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	model: Goal;
}

type Action = 'openActivityLog' | 'duplicate' | 'toggleFavorite' | 'delete';
export type Option = PopupListItem<Action>;

export default class GoalInsightsIndexComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/goal-logs') declare mmGoalLogsService: Service.ModelManager.GoalLogs;
	@service('logger') declare loggerService: Service.Logger;
	@service('timeframe') declare timeframeService: Service.Timeframe;
	@service('infolayer') declare infolayerService: Service.Infolayer;

	@tracked chartState: GoalInsightsChartState;
	@tracked valueRows: GoalValueRow[] = [];
	@tracked referencePoint: DChartPrimaryPoint | null = null;

	origin = 'goals_detail';
	targetEndDateFormat = TargetEndDateFormat;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.chartState = new GoalInsightsChartState();

		this.analyticsService.event(TrackingEvent.GOAL_INSIGHTS_OPEN);

		void this.model.loadMainMetricLimitations();

		if (!this.model.dateRange)
			this.loggerService.logError('[Goal Details]: Date range missing', {
				goalId: this.model.id,
				blockId: this.model.blockId,
			});
	}

	get model() {
		return this.args.model;
	}

	get parentGoal() {
		return this.model.parentGoal;
	}

	get parentGoalData() {
		// will be always available bcos goal is saved
		return this.parentGoal?.primaryDataModel;
	}

	get iconClass() {
		return this.model.isObjectiveType ? 'Flag' : 'Target';
	}

	get goalData() {
		// will be always available bcos goal is saved
		return this.model.primaryDataModel!;
	}

	get canEditGoal() {
		return this.model.canEdit;
	}

	get canEditPrivacy() {
		return this.canEditGoal && this.model.isGoalType && !this.isPartOfHierarchy;
	}

	get isPartOfHierarchy() {
		return !!this.model.parentGoal || this.model.children.length > 0;
	}

	get privacyTooltip(): string {
		if (this.isPartOfHierarchy) {
			return 'Goals that have sub-goals or are added as sub-goals must be public.';
		}
		return '';
	}

	get showSubgoals() {
		return this.mmGoalsService.isGoalsOKRsEnabled;
	}

	get showSubgoalsInTopSection() {
		return this.showSubgoals && this.model.isSubgoalsProgressGoal;
	}

	get showSubgoalsInBottomSection() {
		return this.showSubgoals && !this.showSubgoalsInTopSection;
	}

	get showDataVisualization() {
		return this.model.isAutomaticGoal && this.model.mainMetricSetup && this.model.dateRange;
	}

	get goalRenamedTrackingObj() {
		return { event: TrackingEvent.GOAL_RENAME, data: { goal_id: this.model.id } };
	}

	@cached
	get breadcrumbItems() {
		// Build breadcrumbs from parent chain only (exclude current goal)
		const items: { id: string; title: string }[] = [];
		let goal: Goal | null | undefined = this.model.parentGoal;
		while (goal) {
			items.unshift({
				id: goal.id!,
				title: goal.title || 'Untitled',
			});
			goal = goal.parentGoal;
		}
		return items;
	}

	get customEndDateString() {
		return this.model.endDate || 'Not set';
	}

	get isReferenceValueLoading() {
		return !!this.mmGoalsService.isReferenceValueLoading;
	}

	get updatedTextFromGoalLogs() {
		const goalLogs = this.mmGoalLogsService.getLogsForGoal(this.model.id!);

		if (goalLogs.length === 0) {
			return 'No updates yet';
		}

		const latestLog = goalLogs[0]!;
		return `Updated ${latestLog.createdTextRelative}${latestLog.createdByUser ? ` by ${latestLog.createdByUser.name}` : ''}`;
	}

	@cached
	get valueRowsByTimeUnit() {
		return groupValueRowsByTimeUnit(this.valueRows);
	}

	get isContinousTimeframeDisabled() {
		return (
			this.model.isObjectiveType ||
			this.model.isSubgoalsProgressGoal ||
			(!this.model.isTimeframeContinuous && this.model.isGoalType)
		);
	}

	@action
	openBreadcrumbItem(id: string) {
		const goalModel = this.mmGoalsService.findModel(id);
		if (goalModel) {
			this.mmGoalsService.openGoalInsightsPage(goalModel);
		}
	}

	@action
	openOKRsPage() {
		this.navigationService.transitionToRoute('goals.strategy');
	}

	@action
	openUpdateGoalStatus() {
		this.mmGoalsService.openUpdateProgressPopup(this.model);
	}

	@action
	changeTimeframe(timeframe: Timeframe | null): void {
		if (this.model.timeframe === timeframe) {
			return;
		}

		const showConfirmPopup = (isEndDateBeforeTimeframeStart: boolean) => {
			let confirmationText =
				'Changing the timeframe will recalculate all historical target values';
			if (isEndDateBeforeTimeframeStart) {
				confirmationText +=
					' and remove any custom due date set before the new timeframe start';
			}

			this.popupService.openConfirmPopup({
				title: 'Change timeframe',
				text: confirmationText + '. Do you want to proceed?',
				isWarning: true,
				onConfirm: () => {
					this.model.timeframe = timeframe || null;

					if (isEndDateBeforeTimeframeStart) {
						this.model.endDate = null;
					}

					this.triggerRecalculationAndSave();
				},
			});
		};

		if (timeframe) {
			void this.timeframeService.getTimeframePeriods(timeframe).then((periodsData) => {
				const period = periodsData
					? this.timeframeService.findPeriod(periodsData, timeframe.type)
					: null;
				const willRemoveEndDate = isTimestampBefore(
					this.model.endDateTs,
					period?.periodStartTS ?? null,
				);
				showConfirmPopup(willRemoveEndDate);
			});
		} else {
			showConfirmPopup(false);
		}
	}

	@action
	changeEndDate(date: string) {
		if (this.model.endDate === date) {
			return;
		}

		const newEndDateTs = this.mmGoalsService.getTargetEndDateTimestamp(date);
		const previousValue = this.model.endDate;
		if (isTimestampBefore(newEndDateTs, this.model.startDateTsFromTimeframe)) {
			this.infolayerService.error(
				'The selected end date is before the timeframe start date. Please select a later date.',
			);
			this.model.endDate = date;
			next(() => {
				this.model.endDate = previousValue;
			});
			return;
		}

		this.model.endDate = date || null;

		this.popupService.openConfirmPopup({
			title: 'Change end date',
			text: 'Changing the end date will recalculate all historical target values. Do you want to proceed?',
			isWarning: true,
			onConfirm: () => {
				this.triggerRecalculationAndSave();
			},
			onReject: () => {
				this.model.endDate = previousValue;
			},
		});
	}

	@action
	revertManualGoalStatus() {
		void this.mmGoalsService.requestStatusRevert(this.model.id!);
	}

	@action
	openParentGoalDetailsPage() {
		if (this.parentGoal) {
			this.mmGoalsService.openGoalInsightsPage(this.parentGoal);
		}
	}

	private recalculateAllValuesAndSave() {
		if (this.model.isTargetOtherMetric) {
			return;
		}

		recalculateAllValues(
			this.valueRowsByTimeUnit,
			this.model.primaryGranulation,
			this.model.isTargetValuePercentFormat,
			this.model.targetValue!,
			this.model.startDateTsFromTimeframe,
			this.model.valuesLastDateTs,
			this.model.useEndDateForCalculations,
			this.model.mainMetricType,
			this.referencePoint?.value,
		);

		this.valueRows.forEach((r) => {
			r.markClean();
		});

		this.mmGoalsService.markRowHiddenAndClearValue(this.valueRows, this.model.valuesLastDateTs);

		const newHistoryValues = getHistoryValuesFromValueRows(this.valueRowsByTimeUnit);
		if (!equal(this.model.historyValues, newHistoryValues)) {
			this.analyticsService.event(TrackingEvent.GOAL_TIMETABLE_CHANGED);
		}
		this.model.historyValues = newHistoryValues;

		void this.model.save();
	}

	private fetchAndSetReferenceValueAndRecalculate() {
		this.mmGoalsService
			.fetchReferenceValue(this.model.mainMetricSetup, this.model.primaryGranulation)
			.then((referencePoint) => {
				this.referencePoint = referencePoint;
			})
			.catch(() => {
				// Fallback to default value
				this.referencePoint = null;
			})
			.finally(() => {
				this.recalculateAllValuesAndSave();
			});
	}

	private triggerRecalculationAndSave() {
		this.model.checkSuccessCondition();

		void this.mmGoalsService.checkToRebuildFullForm(this.valueRows, this.model).then(() => {
			this.valueRows = this.mmGoalsService.updateFormLayout(
				this.valueRows,
				this.valueRowsByTimeUnit,
				this.model.isMainMetricPercentage,
				this.model.isMainMetricInverted,
			);

			this.mmGoalsService.markRowHiddenAndClearValue(
				this.valueRows,
				this.model.valuesLastDateTs,
			);

			this.fetchAndSetReferenceValueAndRecalculate();
		});
	}

	// ----- Panel-notifications -----

	@tracked notificationSaveInProgress = false;

	@action
	toggleIsDeliveryOn() {
		if (this.notificationSaveInProgress) {
			return;
		}

		this.model.isDeliveryOn = !this.model.isDeliveryOn;

		void this.triggerModelNotificationsSave();
	}

	@action
	applyOwnerNotificationSetting(scheduleType: GoalOwnerScheduleType) {
		if (this.notificationSaveInProgress) {
			return;
		}

		this.model.ownerScheduleType = scheduleType;

		void this.triggerModelNotificationsSave();
	}

	@action
	applyNotificationSettings(
		delivery: GoalDeliveryType[],
		recipients: string[],
		slackChannels: Slack.Channel[],
		scheduleTypes: ScheduleType[],
	) {
		const model = this.args.model;

		model.delivery = delivery;
		model.recipients = recipients;
		model.slackChannels = slackChannels;
		model.scheduleTypes = scheduleTypes;

		void this.triggerModelNotificationsSave();
	}

	private async triggerModelNotificationsSave() {
		this.notificationSaveInProgress = true;

		await this.model.saveAndCatch();

		this.notificationSaveInProgress = false;
	}
	<template>
		<div class="goal-insights">
			<div class="goal-insights__left">
				<div class="section section--first">
					<div class="top">
						<div class="breadcrumbs">
							<a class="okrs" {{on "click" this.openOKRsPage}}>OKRs</a>
							{{#each this.breadcrumbItems as |item|}}
								<CommonUiIconComponent @name="ChevronRight" @size="sm" />
								<a {{on "click" (fn this.openBreadcrumbItem item.id)}}>
									{{item.title}}
								</a>
							{{/each}}
							<CommonUiIconComponent @name="ChevronRight" @size="sm" />
						</div>
						<GoalsInsightsToolbarComponent @model={{this.model}} />
						{{!-- <div class="actions">
                    {{#if this.canEditGoal}}
                        <a class="action action--edit" {{on "click" this.editGoal}}>
                            <Common::UiIcon @name="Preferences" @size="md" />
                            Progress settings
                        </a>
                    {{/if}}
                    {{#if this.contextOptions}}
                        <a class="action action--context {{if this.isContextOpen "active"}}" {{on "click" this.openContextMenu}}>
                            <Common::UiIcon @name="Ellipsis" @size="md" />
                        </a>
                    {{/if}}
                </div> --}}
					</div>
					<div class={{this.goalData.currentProgressClass}}>
						<div class="overview">

							<div class="target">
								<CommonUiIconComponent @name={{this.iconClass}} @size="xl" />
							</div>

							<div class="title">
								<EditableModelPropertyComponent
									@model={{this.model}}
									@canEdit={{this.canEditGoal}}
									@maxlength={{80}}
									@trackingObj={{this.goalRenamedTrackingObj}}
								/>
							</div>

							<GoalsInsightsDescriptionComponent @model={{this.model}} />

						</div>

						<div class="main-progress">
							<GoalsProgressComponent
								@model={{this.model}}
								@goalData={{this.goalData}}
								@size="large"
							/>
						</div>
					</div>

					{{#if this.showDataVisualization}}
						<GoalsInsightsDataVisualizationComponent
							@forceLoadingState={{false}}
							@chartState={{this.chartState}}
							@origin={{this.origin}}
							@model={{this.model}}
						/>

						<GoalsInsightsAdditionalBoxesComponent
							@metricSetup={{this.model.mainMetricSetup}}
							@chartState={{this.chartState}}
							@dateRange={{this.model.dateRange}}
							@forceLoadingState={{false}}
							@origin={{this.origin}}
						/>
					{{/if}}

					{{#if this.showSubgoalsInTopSection}}
						<GoalsInsightsSubgoalsComponent @model={{this.model}} />
					{{/if}}
				</div>

				{{#if this.showSubgoalsInBottomSection}}
					<div class="section">
						<GoalsInsightsSubgoalsComponent @model={{this.model}} />
					</div>
				{{/if}}

				<GoalsInsightsLogsComponent @model={{this.model}} />

			</div>

			<div class="goal-insights__right">
				<div class="panel">
					<div class="panel-header">About this {{this.model.goalTypeText}}</div>
					<div class="panel-status-row">
						{{#if this.goalData.status}}
							<GoalsStatusComponent @status={{this.goalData.status}} @size="large" />
						{{else}}
							No status yet
						{{/if}}
					</div>
					<div class="panel-updated">{{this.updatedTextFromGoalLogs}}</div>
					{{#if this.canEditGoal}}
						<div
							class="button theme-blue-outlined"
							role="button"
							{{on "click" this.openUpdateGoalStatus}}
						>
							<CommonUiIconComponent @name="Pencil" @size="md" />
							Update status
						</div>
					{{/if}}
					{{#if this.model.statusOverride}}
						<div class="status-manual">
							<CommonUiIconComponent @name="InfoFill" @size="sm" />
							<p>
								Status won’t auto-update because it was manually set.
								{{#if this.canEditGoal}}
									<a
										role="button"
										class="link revert-link"
										{{on "click" this.revertManualGoalStatus}}
									>
										Revert
									</a>
								{{/if}}
							</p>
						</div>
					{{/if}}
					<div class="goal-notifications">
						{{#if this.model.isManualGoal}}
							<GoalNotificationsOwnerComponent
								@isDeliveryOn={{this.model.isDeliveryOn}}
								@scheduleType={{definite this.model.ownerScheduleType}}
								@submitInProgress={{this.notificationSaveInProgress}}
								@optionsAsDropdown={{false}}
								@toggleDelivery={{this.toggleIsDeliveryOn}}
								@changeScheduleType={{this.applyOwnerNotificationSetting}}
							/>
						{{else}}
							<GoalNotificationsSettingsComponent
								@model={{this.model}}
								@toggleDelivery={{this.toggleIsDeliveryOn}}
								@submitInProgress={{this.notificationSaveInProgress}}
								@onApply={{this.applyNotificationSettings}}
							/>
						{{/if}}
					</div>
					<div class="panel-divider"></div>
					<div class="panel-fields">
						{{#if this.isReferenceValueLoading}}
							<BarLoaderComponent />
						{{/if}}
						<div class="panel-field">
							<span class="panel-label">Owner</span>
							<div class="panel-value">
								<GoalInlineOwnerComponent
									@model={{this.model}}
									@canEdit={{this.canEditGoal}}
									@saveOnChange={{true}}
									@isDisabled={{this.isReferenceValueLoading}}
								/>
							</div>
						</div>
						<div class="panel-field">
							<span class="panel-label">Company or accountable team</span>
							<div class="panel-value">
								<GoalInlineGroupComponent
									@model={{this.model}}
									@canEdit={{this.canEditGoal}}
									@saveOnChange={{true}}
									@isDisabled={{this.isReferenceValueLoading}}
								/>
							</div>
						</div>
						<div class="panel-field">
							<span class="panel-label">Timeframe</span>
							<div class="panel-value">
								{{#if this.canEditGoal}}
									<GoalsTimeframeDropdownComponent
										@model={{this.model}}
										@onChange={{this.changeTimeframe}}
										@isDisabled={{this.isReferenceValueLoading}}
										@isContinuousDisabled={{this.isContinousTimeframeDisabled}}
									/>
								{{else}}
									<CommonUiIconComponent
										@name="CalendarClock"
										@size="md"
										@class="panel-avatar"
									/>
									<span>{{this.model.timeframeText}}</span>
								{{/if}}
							</div>
						</div>
						<div class="panel-field">
							<span class="panel-label">Custom due date</span>
							<div class="panel-value">
								{{#if this.canEditGoal}}
									<FormDatetimePickerComponent
										@dateFormat={{this.targetEndDateFormat}}
										@value={{this.model.endDate}}
										@disableTimePicker={{true}}
										@onChange={{this.changeEndDate}}
										@placeholder="Optional"
										@showClearButton={{true}}
										@disablePast={{true}}
										@hideFloatLabel={{true}}
										@isDisabled={{this.isReferenceValueLoading}}
									/>
								{{else}}
									<CommonUiIconComponent
										@name="Calendar"
										@size="md"
										@class="panel-avatar"
									/>
									<span>{{this.customEndDateString}}</span>
								{{/if}}
							</div>
						</div>
						<div class="panel-field">
							<span class="panel-label">Privacy</span>
							<div class="panel-value">
								{{#if this.canEditPrivacy}}
									<GoalsVisibilityInlineComponent
										@model={{this.model}}
										@isDisabled={{this.isReferenceValueLoading}}
									/>
								{{else}}
									<span class="inline-visibility">
										<CommonUiIconComponent
											@name="Globe"
											@size="md"
											@class="panel-avatar"
										/>
										<span>All users</span>
										{{#if this.privacyTooltip}}
											<ToolTipComponent
												@text={{this.privacyTooltip}}
												@position="top"
											/>
										{{/if}}
									</span>
								{{/if}}
							</div>
						</div>
					</div>
					{{#if this.parentGoal}}
						<div class="panel-divider"></div>
						<span class="panel-label">Parent {{this.parentGoal.goalTypeText}}</span>

						<div
							class="panel-parent-goal"
							role="button"
							{{on "click" this.openParentGoalDetailsPage}}
						>
							<div class="title">{{this.parentGoal.title}}</div>
							<div class="status-owner">
								<span class="status-timeframe">
									{{#if this.parentGoalData}}
										<GoalsStatusComponent
											@status={{this.parentGoalData.status}}
										/>
										<span class="bullet"></span>
									{{/if}}
									<span>{{this.parentGoal.timeframeText}}</span>
								</span>
								<div class="owner">
									<OwnerSelectComponent
										@model={{this.parentGoal}}
										@isOwnerNameHidden={{true}}
									/>
								</div>
							</div>
						</div>
					{{/if}}
				</div>
			</div>
		</div>
	</template>
}
